import { Preferences } from '@capacitor/preferences';
import MessengerService, {
  IMessengerMessage,
  IMessengerReplyPreview,
  IMessengerUser,
} from '@/services/messenger.service';
import { isNetworkConnected } from '@/utils/network-status';
import store from '@/store';

export type MessengerOutboxStatus = 'queued' | 'sending' | 'failed';

export interface IMessengerOutboxItem {
  localId: string;
  /** Отрицательный стабильный id для ключей/UI. */
  tempId: number;
  chatId: number;
  chatType?: 'DIRECT' | 'SUPPORT' | 'GROUP' | 'PUBLIC' | 'SYSTEM';
  body: string;
  replyToId?: number;
  replyTo?: IMessengerReplyPreview | null;
  createdAt: string;
  status: MessengerOutboxStatus;
  error?: string;
  hasImage: boolean;
  localPreviewUrl?: string | null;
}

export interface IMessengerLocalMessage extends IMessengerMessage {
  localId?: string;
  localStatus?: MessengerOutboxStatus;
  localError?: string;
  localPreviewUrl?: string | null;
}

export type MessengerOutboxListener = (items: IMessengerOutboxItem[]) => void;
export type MessengerOutboxSentListener = (event: { localId: string; chatId: number; message: IMessengerMessage }) => void;

const STORAGE_KEY = 'messenger-outbox-v1';

type PersistedOutboxItem = Omit<IMessengerOutboxItem, 'localPreviewUrl'>;

let items: IMessengerOutboxItem[] = [];
let hydrated = false;
let flushing = false;
let networkWatchStarted = false;
let tempIdSeq = 0;

const imageFiles = new Map<string, File>();
const listeners = new Set<MessengerOutboxListener>();
const sentListeners = new Set<MessengerOutboxSentListener>();
const service = new MessengerService();

function nextTempId(): number {
  tempIdSeq = (tempIdSeq + 1) % 1000;
  return -(Date.now() * 1000 + tempIdSeq);
}

function currentAuthor(): IMessengerUser | null {
  const account = store.getters.account;
  if (!account?.login) return null;
  return {
    id: account.id,
    login: account.login,
    firstName: account.firstName,
    lastName: account.lastName,
    imageUrl: account.imageUrl,
  };
}

function notify(): void {
  const snapshot = items.map(item => ({ ...item }));
  listeners.forEach(listener => {
    try {
      listener(snapshot);
    } catch (error) {
      console.warn('Messenger outbox listener failed', error);
    }
  });
}

function toPersisted(item: IMessengerOutboxItem): PersistedOutboxItem {
  return {
    localId: item.localId,
    tempId: item.tempId,
    chatId: item.chatId,
    body: item.body,
    replyToId: item.replyToId,
    replyTo: item.replyTo,
    createdAt: item.createdAt,
    status: item.status === 'sending' ? 'queued' : item.status,
    error: item.error,
    hasImage: item.hasImage,
  };
}

async function persist(): Promise<void> {
  try {
    await Preferences.set({
      key: STORAGE_KEY,
      value: JSON.stringify(items.map(toPersisted)),
    });
  } catch (error) {
    console.warn('Messenger outbox persist failed', error);
  }
}

export function isNetworkError(error: unknown): boolean {
  const err = error as { response?: unknown; code?: string; message?: string };
  if (!err) return true;
  if (err.response == null) return true;
  if (err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED') return true;
  const message = String(err.message || '').toLowerCase();
  return message.includes('network') || message.includes('timeout') || message.includes('offline');
}

export function outboxItemToLocalMessage(item: IMessengerOutboxItem): IMessengerLocalMessage {
  return {
    id: item.tempId,
    chatId: item.chatId,
    author: currentAuthor(),
    body: item.body,
    createdDate: item.createdAt,
    deleted: false,
    replyTo: item.replyTo || null,
    attachments: [],
    reactions: {},
    localId: item.localId,
    localStatus: item.status,
    localError: item.error,
    localPreviewUrl: item.localPreviewUrl || null,
  };
}

export function getMessengerOutboxItems(): IMessengerOutboxItem[] {
  return items.map(item => ({ ...item }));
}

export function getMessengerOutboxForChat(chatId: number): IMessengerOutboxItem[] {
  return items.filter(item => item.chatId === chatId).map(item => ({ ...item }));
}

export function subscribeMessengerOutbox(listener: MessengerOutboxListener): () => void {
  listeners.add(listener);
  listener(getMessengerOutboxItems());
  return () => {
    listeners.delete(listener);
  };
}

export function subscribeMessengerOutboxSent(listener: MessengerOutboxSentListener): () => void {
  sentListeners.add(listener);
  return () => {
    sentListeners.delete(listener);
  };
}

function ensureNetworkWatch(): void {
  if (networkWatchStarted || typeof window === 'undefined') return;
  networkWatchStarted = true;
  window.addEventListener('online', () => {
    void flushMessengerOutbox();
  });
  void import('@capacitor/network')
    .then(({ Network }) =>
      Network.addListener('networkStatusChange', status => {
        if (status.connected) void flushMessengerOutbox();
      })
    )
    .catch(() => {
      /* web without plugin */
    });
}

export async function hydrateMessengerOutbox(): Promise<void> {
  if (hydrated) return;
  hydrated = true;
  ensureNetworkWatch();
  try {
    const { value } = await Preferences.get({ key: STORAGE_KEY });
    if (!value) {
      items = [];
      return;
    }
    const parsed = JSON.parse(value) as PersistedOutboxItem[];
    items = (Array.isArray(parsed) ? parsed : []).map(item => {
      const hasFile = imageFiles.has(item.localId);
      const restored: IMessengerOutboxItem = {
        ...item,
        status: item.status === 'sending' ? 'queued' : item.status,
        localPreviewUrl: null,
      };
      if (item.hasImage && !hasFile) {
        restored.status = 'failed';
        restored.error = 'Вложение недоступно после перезапуска. Отправьте снова.';
      }
      return restored;
    });
  } catch (error) {
    console.warn('Messenger outbox hydrate failed', error);
    items = [];
  }
  notify();
  void flushMessengerOutbox();
}

export async function enqueueMessengerOutbox(input: {
  chatId: number;
  chatType?: IMessengerOutboxItem['chatType'];
  body: string;
  image?: File | null;
  replyToId?: number;
  replyTo?: IMessengerReplyPreview | null;
}): Promise<IMessengerOutboxItem> {
  await hydrateMessengerOutbox();
  const localId =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `local-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  let localPreviewUrl: string | null = null;
  if (input.image) {
    imageFiles.set(localId, input.image);
    localPreviewUrl = URL.createObjectURL(input.image);
  }
  const item: IMessengerOutboxItem = {
    localId,
    tempId: nextTempId(),
    chatId: input.chatId,
    chatType: input.chatType,
    body: input.body || '',
    replyToId: input.replyToId,
    replyTo: input.replyTo || null,
    createdAt: new Date().toISOString(),
    status: 'queued',
    hasImage: !!input.image,
    localPreviewUrl,
  };
  items = [...items, item];
  await persist();
  notify();
  void flushMessengerOutbox();
  return item;
}

export async function cancelMessengerOutbox(localId: string): Promise<void> {
  await hydrateMessengerOutbox();
  const item = items.find(entry => entry.localId === localId);
  if (!item) return;
  if (item.status === 'sending') return;
  if (item.localPreviewUrl) {
    URL.revokeObjectURL(item.localPreviewUrl);
  }
  imageFiles.delete(localId);
  items = items.filter(entry => entry.localId !== localId);
  await persist();
  notify();
}

export async function retryMessengerOutbox(localId: string): Promise<void> {
  await hydrateMessengerOutbox();
  const idx = items.findIndex(entry => entry.localId === localId);
  if (idx < 0) return;
  const item = items[idx];
  if (item.status === 'sending') return;
  if (item.hasImage && !imageFiles.has(localId)) {
    items[idx] = {
      ...item,
      status: 'failed',
      error: 'Вложение недоступно. Отправьте сообщение заново.',
    };
    await persist();
    notify();
    return;
  }
  items[idx] = { ...item, status: 'queued', error: undefined };
  await persist();
  notify();
  await flushMessengerOutbox();
}

async function sendOne(item: IMessengerOutboxItem): Promise<void> {
  const online = await isNetworkConnected();
  if (!online) {
    const idx = items.findIndex(entry => entry.localId === item.localId);
    if (idx >= 0 && items[idx].status === 'sending') {
      items[idx] = { ...items[idx], status: 'queued', error: undefined };
      await persist();
      notify();
    }
    return;
  }

  const image = item.hasImage ? imageFiles.get(item.localId) : undefined;
  if (item.hasImage && !image) {
    const idx = items.findIndex(entry => entry.localId === item.localId);
    if (idx >= 0) {
      items[idx] = {
        ...items[idx],
        status: 'failed',
        error: 'Вложение недоступно. Отправьте сообщение заново.',
      };
      await persist();
      notify();
    }
    return;
  }

  try {
    const message = await service.sendMessage(
      item.chatId,
      item.body,
      image,
      undefined,
      item.replyToId,
      item.chatType
    );
    if (item.localPreviewUrl) {
      URL.revokeObjectURL(item.localPreviewUrl);
    }
    imageFiles.delete(item.localId);
    items = items.filter(entry => entry.localId !== item.localId);
    await persist();
    notify();
    sentListeners.forEach(listener => {
      try {
        listener({ localId: item.localId, chatId: item.chatId, message });
      } catch (error) {
        console.warn('Messenger outbox sent listener failed', error);
      }
    });
  } catch (error) {
    const idx = items.findIndex(entry => entry.localId === item.localId);
    if (idx < 0) return;
    if (isNetworkError(error)) {
      const stillOnline = await isNetworkConnected();
      items[idx] = {
        ...items[idx],
        status: stillOnline ? 'failed' : 'queued',
        error: stillOnline ? 'Не удалось отправить. Повторите отправку.' : undefined,
      };
    } else {
      const err = error as { response?: { data?: { title?: string; message?: string; detail?: string } } };
      const detail =
        err?.response?.data?.title ||
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        'Не удалось отправить';
      items[idx] = {
        ...items[idx],
        status: 'failed',
        error: detail,
      };
    }
    await persist();
    notify();
  }
}

/** Отправляет queued-сообщения по порядку; failed только через retry. */
export async function flushMessengerOutbox(chatId?: number): Promise<void> {
  await hydrateMessengerOutbox();
  if (flushing) return;
  flushing = true;
  try {
    ensureNetworkWatch();
    do {
      if (!(await isNetworkConnected())) return;

      const queue = items.filter(
        item => item.status === 'queued' && (chatId == null || item.chatId === chatId)
      );
      if (!queue.length) return;

      for (const item of queue) {
        const current = items.find(entry => entry.localId === item.localId);
        if (!current || current.status !== 'queued') continue;
        const idx = items.findIndex(entry => entry.localId === item.localId);
        if (idx < 0) continue;
        items[idx] = { ...items[idx], status: 'sending', error: undefined };
        notify();
        await sendOne(items[idx]);
        if (!(await isNetworkConnected())) return;
      }
      // Пока шли отправки, могли добавиться новые queued.
    } while (items.some(item => item.status === 'queued' && (chatId == null || item.chatId === chatId)));
  } finally {
    flushing = false;
  }
}
